/**
 * FundingEarmarksDataTable - Account earmarks table using the reusable DataTable component
 * with expandable rows showing linked spend plan details.
 */

// react
import { ReactNode, useContext, useMemo } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate } from 'react-router-dom';

// context
import { ContextProject } from '../../Project/Project.context';

// mui
import {
	alpha,
	Avatar,
	Box,
	Chip,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import { AccountBalance as IconAccount, Payments as IconSpendPlan } from '@mui/icons-material';

// components
import { ColumnDef, DataTable, SortDirection } from '../../../ui/DataTable/DataTable';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// design system

// data
import { GRADIENTS } from '../../../../data/colors';

// utils
import { formatCurrency } from '../../../../utils/currency';

// Types

interface AccountEarmark {
	account: { id: number; name?: string; num?: string; institution?: string; currency?: string };
	balance: number;
	currency: string;
	spendPlans: {
		id: number;
		name: string;
		status: string;
		dateStart?: string;
		dateEnd?: string;
		totalPlanned: number;
	}[];
	totalEarmarked: number;
	totalRemaining: number;
	vouchersByPlan?: Record<
		number,
		{ pending: number; pendingCount: number; redeemed: number; redeemedCount: number }
	>;
	vouchersPending?: number;
	vouchersPendingCount?: number;
	vouchersRedeemed?: number;
	vouchersRedeemedCount?: number;
}

interface FundingEarmarksDataTableProps {
	data: AccountEarmark[];
	isLoading?: boolean;
	searchValue?: string;
	onSearchChange?: (value: string) => void;
	expandAll?: boolean;
	headerActions?: ReactNode;
	orderBy?: string;
	order?: SortDirection;
	onSortChange?: (orderBy: string, order: SortDirection) => void;
}

// Component

export const FundingEarmarksDataTable = ({
	data,
	isLoading = false,
	searchValue = '',
	onSearchChange,
	expandAll,
	headerActions,
	orderBy = 'account',
	order = 'ASC',
	onSortChange,
}: FundingEarmarksDataTableProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { project } = useContext(ContextProject);

	// Column definitions
	const columns: ColumnDef<AccountEarmark>[] = useMemo(
		() => [
			{
				id: 'account',
				label: t('account'),
				sortable: true,
				minWidth: 200,
				render: (row) => (
					<Stack direction="row" alignItems="center" spacing={1.5}>
						<Avatar
							sx={{
								bgcolor: alpha(GRADIENTS.financial.icon, 0.12),
								color: GRADIENTS.financial.icon,
								height: 28,
								width: 28,
								flexShrink: 0,
							}}>
							<IconAccount sx={{ fontSize: 16 }} />
						</Avatar>
						<Box sx={{ minWidth: 0, maxWidth: 200 }}>
							<TruncatedText fontWeight={600} fontSize="1.2rem" text={row.account.name || row.account.num} />
							<Typography variant="body2" color="text.secondary" fontSize="1.2rem">
								{row.account.institution}
								{row.account.currency ? ` · ${row.account.currency}` : ''}
							</Typography>
						</Box>
						{row.spendPlans.length > 0 && (
							<Chip
								label={t('spendPlan.fundingEarmarks.planCount', { count: row.spendPlans.length })}
								size="small"
								sx={{
									fontWeight: 500,
									fontSize: '1.2rem',
									height: 20,
									bgcolor: alpha(GRADIENTS.financial.icon, 0.08),
									color: GRADIENTS.financial.icon,
								}}
							/>
						)}
					</Stack>
				),
			},
			{
				id: 'balance',
				label: t('balance'),
				sortable: true,
				align: 'right' as const,
				minWidth: 100,
				render: (row) => (
					<Typography fontWeight={600} fontSize="1.2rem">
						{formatCurrency({ value: row.balance, currency: row.currency })}
					</Typography>
				),
			},
			{
				id: 'earmarked',
				label: t('earmarked'),
				sortable: true,
				align: 'right' as const,
				minWidth: 100,
				render: (row) => (
					<Typography
						fontWeight={600}
						fontSize="1.2rem"
						color={row.totalEarmarked > 0 ? 'success.main' : 'text.secondary'}>
						{formatCurrency({ value: row.totalEarmarked, currency: row.currency })}
					</Typography>
				),
			},
			{
				id: 'vouchersPending',
				label: t('pending'),
				sortable: true,
				align: 'right' as const,
				minWidth: 90,
				renderHeader: () => (
					<Tooltip title="Vouchers Pending" arrow>
						<span>{t('pending')}</span>
					</Tooltip>
				),
				render: (row) => {
					const amount = row.vouchersPending || 0;
					const count = row.vouchersPendingCount || 0;
					return (
						<Tooltip
							title={`${count} voucher${count !== 1 ? 's' : ''} generated, ready, or verified but not yet redeemed`}
							arrow>
							<Stack alignItems="flex-end" spacing={0}>
								<Typography
									fontWeight={600}
									fontSize="1.2rem"
									color={amount > 0 ? 'warning.main' : 'text.secondary'}>
									{formatCurrency({ value: amount, currency: row.currency })}
								</Typography>
								<Typography variant="caption" color="text.secondary" fontSize="1.2rem">
									{count}
								</Typography>
							</Stack>
						</Tooltip>
					);
				},
			},
			{
				id: 'vouchersRedeemed',
				label: t('redeemed'),
				sortable: true,
				align: 'right' as const,
				minWidth: 90,
				renderHeader: () => (
					<Tooltip title="Vouchers Redeemed" arrow>
						<span>{t('redeemed')}</span>
					</Tooltip>
				),
				render: (row) => {
					const amount = row.vouchersRedeemed || 0;
					const count = row.vouchersRedeemedCount || 0;
					return (
						<Tooltip title={`${count} voucher${count !== 1 ? 's' : ''} redeemed`} arrow>
							<Stack alignItems="flex-end" spacing={0}>
								<Typography
									fontWeight={600}
									fontSize="1.2rem"
									color={amount > 0 ? 'success.main' : 'text.secondary'}>
									{formatCurrency({ value: amount, currency: row.currency })}
								</Typography>
								<Typography variant="caption" color="text.secondary" fontSize="1.2rem">
									{count}
								</Typography>
							</Stack>
						</Tooltip>
					);
				},
			},
			{
				id: 'remaining',
				label: t('remaining'),
				sortable: true,
				align: 'right' as const,
				minWidth: 100,
				render: (row) => {
					const utilization =
						row.balance > 0
							? Math.min(Math.round((row.totalEarmarked / row.balance) * 100), 100)
							: 0;
					const isOverEarmarked = row.totalEarmarked > row.balance;
					return (
						<Tooltip title={`${utilization}% of balance earmarked`} arrow placement="left">
							<Stack alignItems="flex-end" spacing={0.5}>
								<Typography
									fontWeight={600}
									fontSize="1.2rem"
									color={row.totalRemaining < 0 ? 'error.main' : 'text.primary'}>
									{formatCurrency({ value: row.totalRemaining, currency: row.currency })}
								</Typography>
								<LinearProgress
									variant="determinate"
									value={utilization}
									sx={{
										width: '100%',
										height: 4,
										borderRadius: 2,
										bgcolor: alpha(theme.palette.grey[300], 0.5),
										'& .MuiLinearProgress-bar': {
											borderRadius: 2,
											bgcolor: isOverEarmarked
												? theme.palette.error.main
												: utilization > 80
													? theme.palette.warning.main
													: theme.palette.success.main,
										},
									}}
								/>
							</Stack>
						</Tooltip>
					);
				},
			},
		],
		[theme]
	);

	// Expanded row content: table of spend plans linked to this account
	const renderExpanded = (row: AccountEarmark) => {
		if (row.spendPlans.length === 0) {
			return (
				<Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontStyle: 'italic' }}>
				{t('spendPlan.fundingEarmarks.noSpendPlans')}
				</Typography>
			);
		}

		return (
			<Box sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5), px: 4, py: 2 }}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								{t('spendPlan.fundingEarmarks.spendPlanColumn')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								{t('status')}
							</TableCell>
							<TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								{t('period')}
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								{t('earmarked')}
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								<Tooltip title="Vouchers Pending" arrow>
									<span>{t('pending')}</span>
								</Tooltip>
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem' }}>
								<Tooltip title="Vouchers Redeemed" arrow>
									<span>{t('redeemed')}</span>
								</Tooltip>
							</TableCell>
							<TableCell
								sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '1.2rem', width: 120 }}>
								{t('spendPlan.fundingEarmarks.pctOfBalance')}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{row.spendPlans.map((sp) => {
							const pctOfBalance =
								row.balance > 0 ? Math.round((sp.totalPlanned / row.balance) * 100) : 0;
							const handleNavigate = () => {
								if (project?.idWbg) {
									navigate(`/project/${project.idWbg}/spend-plans/spend-planning/${sp.id}`);
								}
							};
							return (
								<TableRow
									key={sp.id}
									hover
									onClick={handleNavigate}
									sx={{
										cursor: 'pointer',
										'&:last-child td': { border: 0 },
										'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
									}}>
									<TableCell>
										<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
											<IconSpendPlan sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
											<TruncatedText
												fontWeight={500}
												fontSize="1.2rem"
												text={sp.name}
												sx={{
													color: 'primary.main',
												maxWidth: 260,
												'&:hover': { textDecoration: 'underline' },
												}}
											/>
										</Stack>
									</TableCell>
									<TableCell>
										<Chip
											label={sp.status}
											size="small"
											sx={{
												fontWeight: 600,
												fontSize: '1.2rem',
												height: 20,
												bgcolor:
													sp.status === 'APPROVED'
														? alpha(theme.palette.success.main, 0.1)
														: sp.status === 'COMPLETED'
															? alpha(theme.palette.success.main, 0.1)
															: sp.status === 'SUBMITTED'
																? alpha(theme.palette.warning.main, 0.1)
																: sp.status === 'DRAFT'
																	? alpha(theme.palette.grey[500], 0.1)
																	: alpha(theme.palette.info.main, 0.1),
												color:
													sp.status === 'APPROVED'
														? 'success.main'
														: sp.status === 'COMPLETED'
															? 'success.main'
															: sp.status === 'SUBMITTED'
																? 'warning.main'
																: sp.status === 'DRAFT'
																	? 'text.secondary'
																	: 'info.main',
											}}
										/>
									</TableCell>
									<TableCell>
										<Typography fontSize="1.2rem" color="text.secondary">
											{sp.dateStart
												? new Date(sp.dateStart).toLocaleDateString('en-US', {
														timeZone: 'UTC',
														month: 'short',
														year: 'numeric',
													})
												: '—'}
											{' – '}
											{sp.dateEnd
												? new Date(sp.dateEnd).toLocaleDateString('en-US', {
														timeZone: 'UTC',
														month: 'short',
														year: 'numeric',
													})
												: '—'}
										</Typography>
									</TableCell>
									<TableCell align="right">
										<Tooltip
											title={`This spend plan earmarks ${formatCurrency({ value: sp.totalPlanned, currency: row.currency })} of the account's ${formatCurrency({ value: row.balance, currency: row.currency })} balance`}
											arrow>
											<Typography fontWeight={600} fontSize="1.2rem">
												{formatCurrency({ value: sp.totalPlanned, currency: row.currency })}
											</Typography>
										</Tooltip>
									</TableCell>
									{(() => {
										const vSummary = row.vouchersByPlan?.[sp.id];
										const pendingAmt = vSummary?.pending || 0;
										const pendingCount = vSummary?.pendingCount || 0;
										const redeemedAmt = vSummary?.redeemed || 0;
										const redeemedCount = vSummary?.redeemedCount || 0;
										return (
											<>
												<TableCell align="right">
													<Stack alignItems="flex-end" spacing={0}>
														<Typography
															fontWeight={600}
															fontSize="1.2rem"
															color={pendingAmt > 0 ? 'warning.main' : 'text.secondary'}>
															{formatCurrency({ value: pendingAmt, currency: row.currency })}
														</Typography>
														<Typography variant="caption" color="text.secondary" fontSize="1.2rem">
															{pendingCount}
														</Typography>
													</Stack>
												</TableCell>
												<TableCell align="right">
													<Stack alignItems="flex-end" spacing={0}>
														<Typography
															fontWeight={600}
															fontSize="1.2rem"
															color={redeemedAmt > 0 ? 'success.main' : 'text.secondary'}>
															{formatCurrency({ value: redeemedAmt, currency: row.currency })}
														</Typography>
														<Typography variant="caption" color="text.secondary" fontSize="1.2rem">
															{redeemedCount}
														</Typography>
													</Stack>
												</TableCell>
											</>
										);
									})()}
									<TableCell>
										<Stack spacing={0.5}>
											<LinearProgress
												variant="determinate"
												value={Math.min(pctOfBalance, 100)}
												sx={{
													height: 6,
													borderRadius: 3,
													bgcolor: alpha(theme.palette.grey[300], 0.4),
													'& .MuiLinearProgress-bar': {
														borderRadius: 3,
														bgcolor: theme.palette.primary.main,
													},
												}}
											/>
											<Typography variant="caption" color="text.secondary" fontSize="1.2rem">
												{pctOfBalance}%
											</Typography>
										</Stack>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</Box>
		);
	};

	return (
		<DataTable<AccountEarmark>
			data={data}
			columns={columns}
			getRowKey={(row) => row.account.id}
			loading={isLoading}
			emptyMessage="No accounts found for this project."
			emptyIcon={<IconAccount sx={{ fontSize: 48 }} />}
			expandable
			expandOnRowClick
			expandAll={expandAll}
			renderExpanded={renderExpanded}
			orderBy={orderBy}
			order={order}
			onSortChange={onSortChange}
			headerActions={headerActions}
			searchPlaceholder={t('spendPlan.fundingEarmarks.searchPlaceholder')}
			searchValue={searchValue}
			onSearchChange={onSearchChange}
		/>
	);
};
